/**
 * Local app settings (volumes). Persistence: localStorage —
 * the app is offline and without accounts.
 */

import { createContext, createSignal, type ParentComponent, useContext } from "solid-js";

const STORAGE_KEY = "settings.v1";

interface Settings {
  /** Background music volume [0, 1]. */
  musicVolume: number;
  /** Effects/instruments volume [0, 1]. */
  sfxVolume: number;
}

const DEFAULTS: Settings = { musicVolume: 0.4, sfxVolume: 1 };

interface SettingsContextValue {
  musicVolume: () => number;
  sfxVolume: () => number;
  setMusicVolume: (value: number) => void;
  setSfxVolume: (value: number) => void;
}

const SettingsCtx = createContext<SettingsContextValue>();

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsCtx);
  if (!ctx) {
    throw new Error("useSettings must be used inside <SettingsProvider>");
  }
  return ctx;
}

function load(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return DEFAULTS;
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) {
      return DEFAULTS;
    }
    const record = parsed as Partial<Record<keyof Settings, unknown>>;
    return {
      musicVolume: clamp01(record.musicVolume, DEFAULTS.musicVolume),
      sfxVolume: clamp01(record.sfxVolume, DEFAULTS.sfxVolume),
    };
  } catch {
    return DEFAULTS;
  }
}

function clamp01(value: unknown, fallback: number): number {
  return typeof value === "number" && Number.isFinite(value)
    ? Math.min(1, Math.max(0, value))
    : fallback;
}

export const SettingsProvider: ParentComponent = (props) => {
  const initial = load();
  const [musicVolume, setMusic] = createSignal(initial.musicVolume);
  const [sfxVolume, setSfx] = createSignal(initial.sfxVolume);

  const persist = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ musicVolume: musicVolume(), sfxVolume: sfxVolume() }),
      );
    } catch {
      // localStorage full or blocked: the settings live only in memory.
    }
  };

  const value: SettingsContextValue = {
    musicVolume,
    sfxVolume,
    setMusicVolume: (v) => {
      setMusic(clamp01(v, DEFAULTS.musicVolume));
      persist();
    },
    setSfxVolume: (v) => {
      setSfx(clamp01(v, DEFAULTS.sfxVolume));
      persist();
    },
  };

  return <SettingsCtx.Provider value={value}>{props.children}</SettingsCtx.Provider>;
};
